import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Button, Form, Spinner, Alert } from 'react-bootstrap';
import apiService from '../services/api';
import BrandManagement from './metadata/BrandManagement';
import ReconciliationReportTypeManagement from './metadata/ReconciliationReportTypeManagement';
import UnitOfMeasureManagement from './metadata/UnitOfMeasureManagement';
import StorageLocationTypeManagement from './metadata/StorageLocationTypeManagement';
import SaleTagValueManagement from './metadata/SaleTagValueManagement';
import SaleDeletionReasonManagement from './metadata/SaleDeletionReasonManagement';
import ProductManagement from './metadata/ProductManagement';

const SystemMetadata = () => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [loadingOrganizations, setLoadingOrganizations] = useState(true);
    const [error, setError] = useState('');
    const [activeSubView, setActiveSubView] = useState(null);

    useEffect(() => {
        loadOrganizations();
    }, []);

    const loadOrganizations = async () => {
        setLoadingOrganizations(true);
        setError('');

        try {
            const response = await apiService.getOrganizations(1, 1000, '');
            if (response.success) {
                const activeOrgs = (response.data || []).filter(
                    org => (org.subscription_status || '').toLowerCase() === 'active'
                );
                setOrganizations(activeOrgs);
            } else {
                setError(response.error || 'Failed to load organizations');
            }
        } catch (error) {
            console.error('Error loading organizations:', error);
            setError('Failed to load organizations');
        } finally {
            setLoadingOrganizations(false);
        }
    };

    const handleOrganizationChange = (e) => {
        const orgId = e.target.value;
        if (orgId) {
            const org = organizations.find(o => o.id === parseInt(orgId));
            setSelectedOrganization(org);
        } else {
            setSelectedOrganization(null);
        }
    };

    const metadataButtons = [
        { key: 'brand', label: 'Brand', icon: 'bi-tag' },
        { key: 'product', label: 'Products', icon: 'bi-box-seam' },
        { key: 'unit_of_measure', label: 'Units of Measure', icon: 'bi-rulers' },
        { key: 'storage_location_type', label: 'Storage Location Type', icon: 'bi-geo-alt' },
        { key: 'reconciliation_report_type', label: 'Reconciliation Report Type', icon: 'bi-file-earmark-bar-graph' },
        { key: 'sale_tag_value', label: 'Sale Tag Value', icon: 'bi-bookmark-star' },
        { key: 'sale_deletion_reason', label: 'Sale Deletion Reasons', icon: 'bi-trash' },
    ];

    const handleBack = () => setActiveSubView(null);

    if (activeSubView && selectedOrganization) {
        switch (activeSubView) {
            case 'brand':
                return <BrandManagement organization={selectedOrganization} onBack={handleBack} />;
            case 'product':
                return <ProductManagement organization={selectedOrganization} onBack={handleBack} />;
            case 'reconciliation_report_type':
                return <ReconciliationReportTypeManagement organization={selectedOrganization} onBack={handleBack} />;
            case 'unit_of_measure':
                return <UnitOfMeasureManagement organization={selectedOrganization} onBack={handleBack} />;
            case 'storage_location_type':
                return <StorageLocationTypeManagement organization={selectedOrganization} onBack={handleBack} />;
            case 'sale_tag_value':
                return <SaleTagValueManagement organization={selectedOrganization} onBack={handleBack} />;
            case 'sale_deletion_reason':
                return <SaleDeletionReasonManagement organization={selectedOrganization} onBack={handleBack} />;
            default:
                break;
        }
    }

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2><i className="bi bi-database-gear me-2"></i>System Metadata</h2>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>{error}
                </Alert>
            )}

            <Card className="mb-4">
                <Card.Header>
                    <i className="bi bi-building me-2"></i>Select Organization
                </Card.Header>
                <Card.Body>
                    <Form.Group>
                        <Form.Label>Organization</Form.Label>
                        {loadingOrganizations ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading organizations...
                            </div>
                        ) : (
                            <Form.Select
                                value={selectedOrganization?.id || ''}
                                onChange={handleOrganizationChange}
                            >
                                <option value="">-- Select an Organization --</option>
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </Form.Group>
                </Card.Body>
            </Card>

            {selectedOrganization && (
                <Card>
                    <Card.Header>
                        <i className="bi bi-grid me-2"></i>Metadata for {selectedOrganization.name}
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-3">
                            {metadataButtons.map(btn => (
                                <Col key={btn.key}>
                                    <Button
                                        variant="outline-primary"
                                        className="w-100 py-3 d-flex flex-column align-items-center"
                                        onClick={() => setActiveSubView(btn.key)}
                                    >
                                        <i className={`bi ${btn.icon} fs-2 mb-2`}></i>
                                        <span>{btn.label}</span>
                                    </Button>
                                </Col>
                            ))}
                        </Row>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default SystemMetadata;
